import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Typography, Box, Divider} from 'views/components';
import {Translate, translate} from 'views/module';

import moment from 'moment';

const PrefsAndAvailabilityView = (props) => {
  const {bioQuestions, setIsComplete} = props;

  const resolvedPrefsAndAvailability = React.useMemo(() => {
    const preferences = bioQuestions?.preferences || null;
    const availability = bioQuestions?.availability || null;
    const hasAnyData = Boolean(preferences && availability);

    if (!hasAnyData) {
      return {
        preferences: null,
        availability: null,
        hasAnyData: false,
      };
    }

    const lengthOfShiftsSource = preferences.lengthOfShifts || {};
    const selectedLengthOfShifts = Object.entries(lengthOfShiftsSource).reduce((accumulator, [key, value]) => {
      if (Boolean(value)) {
        accumulator.push(key);
      }

      return accumulator;
    }, []);

    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const presetOrder = ['MORNING', 'AFTERNOON', 'EVENING', 'OVERNIGHT'];
    const presetRangeLabels = {
      MORNING: translate('caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.WeeklyAvailability.Options.MORNING'),
      AFTERNOON: translate('caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.WeeklyAvailability.Options.AFTERNOON'),
      EVENING: translate('caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.WeeklyAvailability.Options.EVENING'),
      OVERNIGHT: translate('caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.WeeklyAvailability.Options.OVERNIGHT'),
    };
    const rangeSeparator = translate('timeFormat.RangeSeparator');

    const preventRangeWrap = (value) => {
      if (typeof value !== 'string') {
        return '';
      }
      const withNonBreakingHyphen = value.replace(/ - /g, '\u00A0\u2011\u00A0');
      return withNonBreakingHyphen.replace(/ /g, '\u00A0');
    };
    const formatRangeTime = (value) => {
      const time = moment(value, 'HH:mm', true);
      if (!time.isValid()) {
        return value;
      }
      return time.format(translate('timeFormat.Time'));
    };
    const formatCustomRange = (range) => {
      if (!range?.start || !range?.end) {
        return null;
      }
      const startLabel = formatRangeTime(range.start);
      const endLabel = formatRangeTime(range.end);

      if (!startLabel || !endLabel) {
        return null;
      }

      return preventRangeWrap(`${startLabel}${rangeSeparator}${endLabel}`);
    };
    const buildDayRanges = (dayData) => {
      const customRanges = (dayData?.customRanges ?? [])
          .map(formatCustomRange)
          .filter(Boolean);
      const presetRanges = presetOrder
          .filter((key) => dayData?.presetRanges?.[key])
          .map((key) => preventRangeWrap(presetRangeLabels[key]))
          .filter(Boolean);

      return [...customRanges, ...presetRanges];
    };

    const weeklyAvailability = availability.weeklyAvailability || {};

    const availabilityRows = daysOfWeek
        .map((day) => {
          const ranges = buildDayRanges(weeklyAvailability[day]);
          if (ranges.length === 0) {
            return null;
          }

          return (
            <Stack key={day} direction='row' gap={2} style={{alignItems: 'flex-start'}}>
              <Stack width={100}>
                <Typography style={{fontWeight: '500', fontSize: 14, lineHeight: 2}}>
                  {`${translate(`caregiver.CaregiverBiography.PrefsAndAvailability.DaysOfWeek.${day}`)}:`}
                </Typography>
              </Stack>
              <Typography style={{flex: 1, fontWeight: '400', fontSize: 14, lineHeight: 2}}>
                {`${ranges.join(', ')}`}
              </Typography>
            </Stack>
          );
        })
        .filter(Boolean);

    const willHoursChangeSource = availability.willHoursChange || {};

    const resolvedPreferences = {
      selectedLengthOfShifts,
      mainTravelMethod: String(preferences.mainTravelMethod).trim(),
      maxTravelTime: preferences.maxTravelTime,
      catAndDogComfort: preferences.catAndDogComfort,
    };

    const resolvedAvailability = {
      availabilityRows,
      didConfirmAvailabilityPreRules: Boolean(availability.didConfirmAvailabilityPreRules),
      workHoursCapacity: availability.workHoursCapacity,
      scheduleTypePref: availability.scheduleTypePref,
      earliestStart: availability.earliestStart,
      didConfirmSubmittedAvailability: Boolean(availability.didConfirmSubmittedAvailability),
      willHoursChangeResponse: willHoursChangeSource.response,
      willHoursChangeDetail: String(willHoursChangeSource.detail || '').trim(),
      didConfirmAvailabilityPostRules: Boolean(availability.didConfirmAvailabilityPostRules),
    };

    return {
      preferences: resolvedPreferences,
      availability: resolvedAvailability,
      hasAnyData,
    };
  }, [bioQuestions]);

  React.useEffect(() => {
    setIsComplete(resolvedPrefsAndAvailability.hasAnyData);
  }, [resolvedPrefsAndAvailability, setIsComplete]);

  if (!resolvedPrefsAndAvailability.hasAnyData) {
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="body2" color="text.secondary">
          <Translate expression="caregiver.CaregiverBiography.PrefsAndAvailability.EmptyState" />
        </Typography>
      </Stack>
    );
  }

  const {preferences, availability} = resolvedPrefsAndAvailability;

  return (
    <Stack spacing={1.5} alignItems="stretch" sx={{width: '100%'}}>
      <Typography variant="subtitle1" sx={{fontWeight: 600}}>
        <Translate expression="caregiver.CaregiverBiography.PrefsAndAvailability.Summary.Title" />
      </Typography>
      <Divider />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #0288d1',
          borderRadius: '12px',
          backgroundColor: '#e1f5fe',
          paddingY: 1,
          paddingX: 1.5,
          gap: 0.5,
        }}
      >
        <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 600}}>
          <Translate expression="caregiver.CaregiverBiography.PrefsAndAvailability.Sections.Preferences" />
        </Typography>
        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression="caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Preferences.LengthOfShifts.Title" />
          <span>: </span>
          {
            preferences.selectedLengthOfShifts.map((x, i) => {
              return (
                <React.Fragment key={i}>
                  <Translate expression={`caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Preferences.LengthOfShifts.Options.${x}`} />
                  {i < preferences.selectedLengthOfShifts.length - 1 && ', '}
                </React.Fragment>
              );
            })
          }
        </Typography>
        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression=
            'caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Preferences.MainTravelMethod.Title'
          />
          <span>: </span>
          <Translate expression={
            `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Preferences.MainTravelMethod.Options.${preferences.mainTravelMethod
            }`
          }
          />
        </Typography>
        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression=
            'caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Preferences.MaxTravelTime.Title'
          />
          <span>: </span>
          <Translate expression={
            `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Preferences.MaxTravelTime.Options.${
              preferences.maxTravelTime
            }`
          }
          />
        </Typography>
        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression=
            'caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Preferences.CatAndDogComfort.Title'
          />
          <span>: </span>
          <Translate expression={
            `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Preferences.CatAndDogComfort.Options.${
              preferences.catAndDogComfort
            }`
          }
          />
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #0288d1',
          borderRadius: '12px',
          backgroundColor: '#e1f5fe',
          paddingY: 1,
          paddingX: 1.5,
          gap: 0.5,
        }}
      >
        <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 600}}>
          <Translate expression="caregiver.CaregiverBiography.PrefsAndAvailability.Sections.Availability" />
        </Typography>

        <Box>
          {availability.availabilityRows}
        </Box>

        {
          availability.availabilityRows.length > 0 && <Divider sx={{my: 2}} />
        }

        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression=
            'caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.WorkHoursCapacity.Title'
          />
          <span>: </span>
          <Translate expression={
            `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.WorkHoursCapacity.Options.${
              availability.workHoursCapacity
            }`
          }
          />
        </Typography>
        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression=
            'caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.ScheduleTypePref.Title'
          />
          <span>: </span>
          <Translate expression={
            `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.ScheduleTypePref.Options.${
              availability.scheduleTypePref
            }`
          }
          />
        </Typography>
        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression=
            'caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.EarliestStart.Title'
          />
          <span>: </span>
          <Translate expression={
            `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.EarliestStart.Options.${
              availability.earliestStart
            }`
          }
          />
        </Typography>
        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression=
            'caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.WillHoursChange.Title'
          />
          <span>: </span>
          <Translate expression={
            `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.WillHoursChange.Response.Options.${
              availability.willHoursChangeResponse
            }`
          }
          />
          {
            (availability.willHoursChangeDetail !== 'OTHER' && Boolean(availability.willHoursChangeDetail)) &&
            <>
              <span> (</span>
              <Translate expression={
                `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.WillHoursChange.Detail.Options.${
                  availability.willHoursChangeDetail
                }`
              }
              />
              <span>)</span>
            </>
          }
        </Typography>
        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression=
            'caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.ConfirmAvailabilityPreRules.Title'
          />
          <span>: </span>
          <Translate expression={
            `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.ConfirmAvailabilityPreRules.Options.${
              availability.didConfirmAvailabilityPreRules
            }`
          }
          />
        </Typography>
        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression=
            'caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.ConfirmSubmittedAvailability.Title'
          />
          <span>: </span>
          <Translate expression={
            `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.ConfirmSubmittedAvailability.Options.${
              availability.didConfirmSubmittedAvailability
            }`
          }
          />
        </Typography>
        <Typography variant="body2" sx={{color: '#0288d1'}}>
          <Translate expression=
            'caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.ConfirmAvailabilityPostRules.Title'
          />
          <span>: </span>
          <Translate expression={
            `caregiver.CaregiverBiography.PrefsAndAvailability.Labels.Availability.ConfirmAvailabilityPostRules.Options.${
              availability.didConfirmAvailabilityPostRules
            }`
          }
          />
        </Typography>
      </Box>
    </Stack>
  );
};

PrefsAndAvailabilityView.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  setIsComplete: PropTypes.func.isRequired,
};

export default React.memo(PrefsAndAvailabilityView);
